import { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Typography, Stack, FormControl, InputLabel, Select as MuiSelect, MenuItem, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import DataTable from '../../components/DataTable';
import { useCompanyDataList } from '../../stores/companyStore';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useNotifier } from '../../components/Notifier';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import { useLanguage } from '../../utils/i18n';

export default function ChargingManagement() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    try {
      const stored = window.localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return [String(stored)];
      }
    } catch {}
    return [];
  });

  const chargingColumns = useMemo(
    () => [
      { field: 'licensePlate', headerName: t('reports.licensePlate', 'License Plate'), width: 150 },
      { field: 'vehicleType', headerName: t('general.vehicleType', 'Vehicle Type'), width: 150 },
      { field: 'station', headerName: t('reportModule.station', 'Station'), width: 200 },
      { field: 'date', headerName: t('general.date', 'Date'), width: 120 },
      { field: 'startTime', headerName: t('achievement.startTime', 'Start Time'), flex: 1 },
      { field: 'endTime', headerName: t('achievement.endTime', 'End Time'), flex: 1 },
      { field: 'duration', headerName: t('reports.duration', 'Duration'), flex: 1 },
      { field: 'energy', headerName: t('mapsTracking.energyKwh', 'Energy (Kwh)'), width: 130 }
    ],
    [t]
  );

  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);

  const [periodType, setPeriodType] = useState('last_week');
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day').startOf('day').second(0));
  const [toDate, setToDate] = useState(dayjs().endOf('day').second(59));
  const [dateError, setDateError] = useState('');

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleOptions = vehiclePickerParams.options;

  const handleVehicleChange = (newSelectedIds) => {
    setSelectedVehicleIds(newSelectedIds);
    const newSelectedObjs = newSelectedIds.map((id) => {
      const existingObj = selectedVehicles.find((obj) => obj.value === id);
      if (existingObj) return existingObj;
      const optionMatch = vehicleOptions.find((opt) => opt.value === id);
      return optionMatch || { value: id, label: String(id) };
    });
    setSelectedVehicles(newSelectedObjs);
  };

  const validateDateRange = (start, end) => {
    if (!start || !end) {
      setDateError(t('mapsTracking.dateCannotBeEmpty', 'Date cannot be empty'));
      return false;
    }
    if (end.isBefore(start)) {
      setDateError(t('reportModule.dateInvalidRange', 'End time cannot be earlier than start time'));
      return false;
    }
    const diff = end.diff(start, 'day');
    if (diff > 30) {
      setDateError(t('reportModule.dateMaxRange', 'Time range maximum is 30 days'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setPeriodType(val);

    const now = dayjs();
    if (val === 'last_24h') {
      setFromDate(now.subtract(24, 'hour').second(0));
      setToDate(now.second(59));
      setDateError('');
    } else if (val === 'last_week') {
      setFromDate(now.subtract(7, 'day').startOf('day').second(0));
      setToDate(now.endOf('day').second(59));
      setDateError('');
    } else if (val === 'last_month') {
      setFromDate(now.subtract(1, 'month').startOf('day').second(0));
      setToDate(now.endOf('day').second(59));
      setDateError('');
    } else {
      setDateError('');
    }
  };

  const handleSearch = () => {
    if (!validateDateRange(fromDate, toDate)) return;
    setLoading(true);

    setTimeout(() => {
      const newRows = [];
      const vIds = selectedVehicleIds.length > 0 ? selectedVehicleIds : [t('reportModule.all', 'ALL')];

      vIds.forEach((vId, idx) => {
        for (let i = 0; i < 2; i++) {
          const licensePlate = vId === 'ALL' ? `B ${1000 + idx * 10 + i} KEL` : selectedVehicles.find((v) => v.value === vId)?.label || vId;
          newRows.push({
            id: `${vId}-${i}-${Date.now()}`,
            licensePlate,
            vehicleType: 'EV Dump Truck',
            station: `${t('reportModule.station', 'Station')} ${Math.floor(Math.random() * 5) + 1}`,
            date: dayjs().subtract(i, 'day').format('YYYY-MM-DD'),
            startTime: '08:00:00',
            endTime: '10:30:00',
            duration: '02:30:00',
            energy: Math.floor(Math.random() * 200) + 100
          });
        }
      });

      setRows(newRows);
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportToExcel = async () => {
    if (rows.length === 0) {
      notify({ message: t('reports.noDataExport', 'No data to export'), severity: 'warning' });
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('reports.chargingManagement', 'Charging Management'));

    worksheet.columns = chargingColumns.map((c) => ({ header: c.headerName, key: c.field, width: 20 }));

    rows.forEach((row) => worksheet.addRow(row));
    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `charging_management_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
          <StorageIcon fontSize="large" color="primary" />
          {t('reports.chargingManagement', 'Charging Management (Dummy)')}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 0.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
        }}
      >
        <Box
          sx={{
            p: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            bgcolor: 'rgba(25, 118, 210, 0.04)',
            borderRadius: 2
          }}
        >
          {/* <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} /> */}
          <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
            {t('reports.chargingManagementDesc', 'Detailed battery charging activity for Electric Vehicles.')}
          </Typography>
          {(localStorage.getItem('rolename') === 'Administrator' ||
            (() => {
              try {
                return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes('"modules_path":"report-catalog"');
              } catch {
                return false;
              }
            })()) && (
            <Box>
              <Button
                startIcon={<ArrowBackIcon fontSize="small" />}
                onClick={() => navigate('/report-catalog?name=report-catalog')}
                size="small"
                variant="contained"
                sx={{
                  bgcolor: 'primary',
                  color: '#ffffff',
                  textTransform: 'none',
                  px: 0.5,
                  py: 0,
                  minWidth: 0,
                  '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                }}
              >
                {t('reports.reportCatalog', 'Report Catalog')}
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <PickerField
                label={t('general.selectCompany', 'Select Company')}
                value={selectedCompanyIds}
                selectedItems={companyOptions.filter((c) => selectedCompanyIds.includes(c.value))}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicleIds([]);
                  setSelectedVehicles([]);
                }}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                multiSelect={true}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <PickerField
                label={t('reports.selectVehicle', 'Select Vehicle')}
                value={selectedVehicleIds}
                selectedItems={selectedVehicles}
                {...vehiclePickerParams}
                options={vehicleOptions}
                onChange={handleVehicleChange}
                dialogTitle={t('reports.selectVehicle', 'Select Vehicle')}
                multiSelect={true}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t('revenue.period', 'Period')}</InputLabel>
              <MuiSelect value={periodType} onChange={handlePeriodChange} label={t('revenue.period', 'Period')}>
                <MenuItem value="last_24h">{t('reports.last24Hours', 'Last 24 Hours')}</MenuItem>
                <MenuItem value="last_week">{t('reports.lastWeek', 'Last Week')}</MenuItem>
                <MenuItem value="last_month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                <MenuItem value="custom">{t('reports.custom', 'Custom')}</MenuItem>
              </MuiSelect>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={t('reports.from', 'From')}
                format="DD/MM/YYYY HH:mm:ss"
                value={fromDate}
                onChange={(val) => {
                  const newV = val ? val.second(0) : val;
                  setFromDate(newV);
                  setPeriodType('custom');
                  validateDateRange(newV, toDate);
                }}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <DateTimePicker
                label={t('reports.to', 'To')}
                format="DD/MM/YYYY HH:mm:ss"
                value={toDate}
                onChange={(val) => {
                  const newV = val ? val.second(59) : val;
                  setToDate(newV);
                  setPeriodType('custom');
                  validateDateRange(fromDate, newV);
                }}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
            </LocalizationProvider>

            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading || !!dateError}
              sx={{ px: 3, py: 1, minWidth: 120 }}
            >
              {t('general.search', 'Search')}
            </Button>
          </Stack>
        </Box>

        {dateError && (
          <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
            {dateError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" onClick={exportToExcel} disabled={rows.length === 0}>
            {t('revenue.exportExcel', 'Export Excel')}
          </Button>
        </Box>

        <Box>
          <DataTable
            rows={rows}
            columns={chargingColumns}
            loading={loading}
            disableRowSelectionOnClick
            hideAddButton={true}
            hideExportButton={true}
            hideSearchField={true}
          />
        </Box>
      </Paper>
    </Box>
  );
}
